/**
 * Primitive design tokens.
 *
 * These are raw values with no meaning attached. Semantic meaning lives in `themes.ts`, so a
 * component never reaches for `slate[700]` directly -- it asks for `border` or `textMuted` and the
 * theme decides. That indirection is what makes a light and a dark theme possible without editing
 * a single component.
 */

/** Neutral slate ramp, used for surfaces, borders and text. */
export const slate = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
} as const;

/**
 * Amber ramp — the Greasemap brand colour.
 *
 * Chosen for the motorsport association rather than the default product blue. Note it sits close
 * to the warning ramp; anything communicating caution must carry an icon or label as well as
 * colour, never colour alone.
 */
export const amber = {
  200: '#fde68a',
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
} as const;

/** Red ramp for destructive and error states. */
export const red = {
  300: '#fca5a5',
  400: '#f87171',
  500: '#ef4444',
  600: '#dc2626',
  700: '#b91c1c',
  800: '#991b1b',
} as const;

/** Green ramp for success and completion. */
export const green = {
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
} as const;

/** Yellow ramp for caution. Distinct from amber, but never the sole signal. */
export const yellow = {
  300: '#fde047',
  400: '#facc15',
  600: '#ca8a04',
  700: '#a16207',
  800: '#854d0e',
} as const;

/**
 * Cyan ramp reserved for "happening right now": a run in progress, a live timing feed, a device
 * currently connected. Deliberately not green, so "live" never reads as "finished successfully".
 */
export const cyan = {
  300: '#67e8f9',
  400: '#22d3ee',
  600: '#0891b2',
  700: '#0e7490',
  800: '#155e75',
} as const;

/** Spacing scale on a 4px base. */
export const spacing = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  '4xl': 64,
} as const;

/** Corner radii. */
export const radius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  pill: 999,
} as const;

/** Font sizes in px. */
export const fontSize = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
  '2xl': 32,
} as const;

/** Font weights. */
export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

/** Line heights, unitless multipliers. */
export const lineHeight = {
  tight: 1.2,
  normal: 1.5,
} as const;

/**
 * The system font stack.
 *
 * No webfont is downloaded. Beyond the performance argument, an event or a race paddock is exactly
 * where the network is worst, and text that never arrives is worse than text in the wrong face.
 */
export const fontFamily = {
  system:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const;

/**
 * Minimum interactive target size in px.
 *
 * Scoring devices are operated quickly, outdoors, sometimes with gloves on. Nothing tappable may
 * be smaller than this.
 */
export const minTouchTarget = 44;

/** Responsive breakpoints in px, mobile first. */
export const breakpoint = {
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

/** Motion durations in ms. */
export const duration = {
  fast: 120,
  normal: 200,
  slow: 320,
} as const;
